import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Ghost, MessageCircle, Zap, Shield, Send, Settings, Sun, Moon, Bell, Check, AlertTriangle, Info, XCircle, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Badge } from '@/components/ui/Badge'
import { Switch } from '@/components/ui/Switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group'
import DeleteButton from '@/components/ui/DeleteButton'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { AlertNotice } from '@/components/ui/AlertNotice'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { useTheme } from '@/contexts/ThemeContext'
import { InboxDemo } from '@/components/inbox'
import { AuthDemo } from '@/components/auth'
import { WidgetDemo } from '@/components/widget'
import { ShortcutsDemo } from '@/components/shortcuts'
import { FormattingDemo } from '@/components/formatting'
import { CsatAnalyticsDemo } from '@/components/analytics/CsatAnalyticsDemo'
import { ChannelsDemo } from '@/components/settings/ChannelsDemo'

export default function Design() {
  const { theme, toggleTheme } = useTheme()
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertVariant, setAlertVariant] = useState<'info' | 'success' | 'warning' | 'error'>('info')

  return (
    <>
      <Head title="Design System" />

      <div className="min-h-screen bg-background p-8 transition-colors duration-300">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <header className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Ghost className="size-12 text-primary" />
              <h1 className="text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Banshee.chat
              </h1>
              {/* Theme Toggle */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggleTheme}
                className="ml-4"
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="size-5" />
                ) : (
                  <Moon className="size-5" />
                )}
              </Button>
            </div>
            <p className="text-muted-foreground text-lg">
              Design System & Component Library
            </p>
          </header>

          {/* Tabs Navigation */}
          <Tabs defaultValue="components" className="w-full">
            <TabsList className="grid w-full grid-cols-8 max-w-4xl mx-auto">
              <TabsTrigger value="components" className="cursor-pointer">Components</TabsTrigger>
              <TabsTrigger value="inbox" className="cursor-pointer">Inbox</TabsTrigger>
              <TabsTrigger value="auth" className="cursor-pointer">Auth</TabsTrigger>
              <TabsTrigger value="widget" className="cursor-pointer">Widget</TabsTrigger>
              <TabsTrigger value="shortcuts" className="cursor-pointer">Shortcuts</TabsTrigger>
              <TabsTrigger value="formatting" className="cursor-pointer">Formatting</TabsTrigger>
              <TabsTrigger value="csat" className="cursor-pointer">CSAT</TabsTrigger>
              <TabsTrigger value="channels" className="cursor-pointer">Channels</TabsTrigger>
            </TabsList>

            {/* Components Tab */}
            <TabsContent value="components" className="mt-8 space-y-12">

          {/* Color Palette */}
          <section className="space-y-4">
            <h2>Color Palette</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-background border" />
                <p className="text-sm text-center">Background</p>
              </div>
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-card border" />
                <p className="text-sm text-center">Card</p>
              </div>
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-primary" />
                <p className="text-sm text-center">Primary (Ghost)</p>
              </div>
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-accent" />
                <p className="text-sm text-center">Accent (Cyan)</p>
              </div>
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-secondary" />
                <p className="text-sm text-center">Secondary</p>
              </div>
              <div className="space-y-2">
                <div className="h-20 rounded-lg bg-destructive" />
                <p className="text-sm text-center">Destructive</p>
              </div>
            </div>
          </section>

          {/* Typography */}
          <section className="space-y-4">
            <h2>Typography</h2>
            <Card>
              <CardContent className="space-y-4 pt-6">
                <h1>Heading 1 - DM Sans</h1>
                <h2>Heading 2 - DM Sans</h2>
                <h3>Heading 3 - DM Sans</h3>
                <p className="text-foreground">Body text - DM Sans. The quick brown fox jumps over the lazy dog.</p>
                <p className="text-muted-foreground">Muted text - DM Sans. The quick brown fox jumps over the lazy dog.</p>
                <code className="block bg-secondary p-2 rounded">Code block - DM Mono</code>
              </CardContent>
            </Card>
          </section>

          {/* Buttons */}
          <section className="space-y-4">
            <h2>Buttons</h2>
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Variants</p>
                  <div className="flex flex-wrap gap-4">
                    <Button>
                      <Send className="size-4" />
                      Default
                    </Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="destructive">Destructive</Button>
                    <Button variant="link">Link</Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Sizes</p>
                  <div className="flex flex-wrap gap-4">
                    <Button size="sm">Small</Button>
                    <Button size="default">Default</Button>
                    <Button size="lg">Large</Button>
                    <Button size="icon">
                      <Settings className="size-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Glow Effects (click to see)</p>
                  <div className="flex flex-wrap gap-4">
                    <Button glow="none">No Glow</Button>
                    <Button glow="click">
                      <Zap className="size-4" />
                      Click Glow
                    </Button>
                    <Button glow="hover">
                      <Zap className="size-4" />
                      Hover Glow
                    </Button>
                    <Button glow="always">
                      <Zap className="size-4" />
                      Always Glow
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Custom CSS Button</p>
                  <button className="btn-primary">
                    <Ghost className="size-4" />
                    Custom Primary Button
                  </button>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Badges */}
          <section className="space-y-4">
            <h2>Badges</h2>
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <Badge>Default</Badge>
                  <Badge variant="secondary">Secondary</Badge>
                  <Badge variant="outline">Outline</Badge>
                  <Badge variant="destructive">Destructive</Badge>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Form Elements */}
          <section className="space-y-4">
            <h2>Form Elements</h2>
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" placeholder="ghost@banshee.chat" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" placeholder="Enter your name" />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="notifications" />
                  <Label htmlFor="notifications">Enable notifications</Label>
                </div>
                <div className="space-y-3 pt-4 border-t">
                  <Label>Radio Group (Avatar Type Example)</Label>
                  <RadioGroup defaultValue="gravatar" className="gap-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <RadioGroupItem value="gravatar" id="demo-gravatar" />
                      <div>
                        <span className="text-sm font-medium">Gravatar</span>
                        <p className="text-xs text-muted-foreground">Uses email to fetch avatar</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <RadioGroupItem value="custom" id="demo-custom" />
                      <div>
                        <span className="text-sm font-medium">Custom upload</span>
                        <p className="text-xs text-muted-foreground">Upload your own photo</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <RadioGroupItem value="initials" id="demo-initials" />
                      <div>
                        <span className="text-sm font-medium">Initials</span>
                        <p className="text-xs text-muted-foreground">Display initials as avatar</p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Dialogs & Confirmations */}
          <section className="space-y-4">
            <h2>Dialogs & Confirmations</h2>
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Delete Button (with confirmation timer)</p>
                  <div className="flex flex-wrap gap-4">
                    <DeleteButton
                      onDelete={() => toast.success('Item deleted!')}
                      initialText="Delete Item"
                    />
                    <DeleteButton
                      onDelete={() => toast.success('Record removed!')}
                      initialText="Remove"
                      confirmText="Click again to confirm"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Confirm Dialog</p>
                  <div className="flex flex-wrap gap-4">
                    <ConfirmDialog
                      trigger={<Button variant="outline">Open Confirm</Button>}
                      title="Confirm Action"
                      description="Are you sure you want to proceed? This action can be undone."
                      confirmText="Yes, proceed"
                      onConfirm={() => toast.success('Action confirmed!')}
                    />
                    <ConfirmDialog
                      trigger={<Button variant="destructive">Destructive Confirm</Button>}
                      title="Delete Account"
                      description="This will permanently delete your account and all associated data. This action cannot be undone."
                      confirmText="Delete Account"
                      destructive
                      onConfirm={() => toast.error('Account deleted!')}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Alert Notices</p>
                  <div className="flex flex-wrap gap-4">
                    <Button variant="outline" onClick={() => { setAlertVariant('info'); setAlertOpen(true); }}>
                      <Info className="size-4" />
                      Info Alert
                    </Button>
                    <Button variant="outline" onClick={() => { setAlertVariant('success'); setAlertOpen(true); }}>
                      <Check className="size-4" />
                      Success Alert
                    </Button>
                    <Button variant="outline" onClick={() => { setAlertVariant('warning'); setAlertOpen(true); }}>
                      <AlertTriangle className="size-4" />
                      Warning Alert
                    </Button>
                    <Button variant="outline" onClick={() => { setAlertVariant('error'); setAlertOpen(true); }}>
                      <Bell className="size-4" />
                      Error Alert
                    </Button>
                  </div>
                  <AlertNotice
                    open={alertOpen}
                    onClose={() => setAlertOpen(false)}
                    title={`${alertVariant.charAt(0).toUpperCase() + alertVariant.slice(1)} Notice`}
                    description={`This is an example ${alertVariant} alert dialog. Use these instead of native browser alerts.`}
                    variant={alertVariant}
                  />
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Toasts */}
          <section className="space-y-4">
            <h2>Toasts</h2>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-3">Click to trigger toasts (bottom center)</p>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" onClick={() => toast('Default toast message', { icon: <Bell className="size-4 text-muted-foreground" /> })}>
                    Default Toast
                  </Button>
                  <Button variant="outline" onClick={() => toast.success('Operation completed successfully!', { icon: <CheckCircle className="size-4 text-green-500" /> })}>
                    <Check className="size-4" />
                    Success Toast
                  </Button>
                  <Button variant="outline" onClick={() => toast.error('Something went wrong!', { icon: <XCircle className="size-4 text-red-500" /> })}>
                    <XCircle className="size-4" />
                    Error Toast
                  </Button>
                  <Button variant="outline" onClick={() => toast.warning('Please review before continuing', { icon: <AlertTriangle className="size-4 text-yellow-500" /> })}>
                    <AlertTriangle className="size-4" />
                    Warning Toast
                  </Button>
                  <Button variant="outline" onClick={() => toast.info('Here is some information', { icon: <Info className="size-4 text-blue-500" /> })}>
                    <Info className="size-4" />
                    Info Toast
                  </Button>
                  <Button variant="outline" onClick={() => {
                    toast.promise(
                      new Promise((resolve) => setTimeout(resolve, 2000)),
                      {
                        loading: 'Loading...',
                        success: 'Data loaded!',
                        error: 'Failed to load',
                      }
                    )
                  }}>
                    <Zap className="size-4" />
                    Promise Toast
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Cards */}
          <section className="space-y-4">
            <h2>Cards</h2>
            <p className="text-sm text-muted-foreground">Default cards (no glow)</p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="size-5 text-primary" />
                    Live Chat
                  </CardTitle>
                  <CardDescription>
                    Real-time customer conversations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Engage with visitors instantly through our lightning-fast chat widget.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button size="sm" className="w-full">Get Started</Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="size-5 text-accent" />
                    Automation
                  </CardTitle>
                  <CardDescription>
                    AI-powered responses
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Let our ghost handle common questions while you focus on complex issues.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button size="sm" variant="secondary" className="w-full">Learn More</Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="size-5 text-success" />
                    Security
                  </CardTitle>
                  <CardDescription>
                    Enterprise-grade protection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Your data is encrypted and protected with bank-level security.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button size="sm" variant="outline" className="w-full">View Details</Button>
                </CardFooter>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground mt-8">Cards with glow effect</p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card glow>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ghost className="size-5 text-primary" />
                    Ghost Glow Card
                  </CardTitle>
                  <CardDescription>
                    With the ghost-glow effect enabled
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Use the <code className="text-xs bg-secondary px-1 rounded">glow</code> prop to enable the glowing effect.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button size="sm" glow="always" className="w-full">Glowing Button</Button>
                </CardFooter>
              </Card>
            </div>
          </section>

          {/* Sample Chat Widget Preview */}
          <section className="space-y-4">
            <h2>Chat Widget Preview</h2>
            <div className="flex justify-end">
              <div className="w-80 rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                {/* Widget Header */}
                <div className="bg-gradient-to-r from-primary/20 to-accent/20 p-4 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Ghost className="size-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">Banshee Support</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <span className="size-2 rounded-full bg-success animate-pulse" />
                        Online
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="bg-card p-4 space-y-4 min-h-[200px]">
                  <div className="flex gap-2">
                    <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <Ghost className="size-4 text-primary" />
                    </div>
                    <div className="bg-secondary rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]">
                      <p className="text-sm">Hey there! How can I help you today?</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%]">
                      <p className="text-sm">I need help with my account</p>
                    </div>
                  </div>
                </div>

                {/* Input */}
                <div className="bg-card border-t border-white/5 p-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      className="flex-1 h-10 bg-secondary/50 border-0"
                    />
                    <Button size="icon" className="size-10 rounded-full">
                      <Send className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

            </TabsContent>

            {/* Inbox Tab */}
            <TabsContent value="inbox" className="mt-8">
              <InboxDemo />
            </TabsContent>

            {/* Auth Tab */}
            <TabsContent value="auth" className="mt-8">
              <AuthDemo />
            </TabsContent>

            {/* Widget Tab */}
            <TabsContent value="widget" className="mt-8">
              <WidgetDemo />
            </TabsContent>

            {/* Shortcuts Tab — BC-18 message shortcuts + AI suggestions */}
            <TabsContent value="shortcuts" className="mt-8">
              <ShortcutsDemo />
            </TabsContent>

            {/* Formatting Tab — BC-23 advanced agent message formatting */}
            <TabsContent value="formatting" className="mt-8">
              <FormattingDemo />
            </TabsContent>

            {/* CSAT Tab — BC-76 proposed analytics layout mockup */}
            <TabsContent value="csat" className="mt-8">
              <CsatAnalyticsDemo />
            </TabsContent>

            {/* Channels Tab — BC-95 proposed per-brand channels layout mockup */}
            <TabsContent value="channels" className="mt-8">
              <div className="space-y-4">
                <div>
                  <h2>Channels (proposed)</h2>
                  <p className="text-sm text-muted-foreground">
                    Per-brand channels with cardinality metadata + add-channel roadmap. Replaces the single "Widget" tab.
                  </p>
                </div>
                <ChannelsDemo />
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <footer className="text-center text-sm text-muted-foreground pt-8 border-t border-white/5">
            <p>Banshee.chat - A lighter, cheaper Crisp alternative</p>
          </footer>
        </div>
      </div>
    </>
  )
}
